import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { clampTooltipPosition, type TooltipPosition } from "./EnhancedTooltip";

/**
 * The single marker for "this product holds contracts your account cannot
 * trade". One visual treatment, two renderings.
 *
 * `interactive` (default): a button that opens the tradability modal.
 * `interactive={false}`: a decorative span with the same pixels and the same
 * accessible label, for hosts that are ALREADY a single interactive control
 * (e.g. the analyzer search row, which is itself a <button>). Nesting a button
 * inside a button or an anchor is invalid HTML and breaks keyboard traversal,
 * so the host picks the rendering -- never taste.
 *
 * The pill must also never be rendered inside a react-router <Link>. Where a
 * product name is a link (analyzer mix rows, both strategy tables), render the
 * pill as a SIBLING of the link.
 */

const PILL_CLASSES =
  "inline-flex items-center gap-1 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-900 shadow-sm ring-1 ring-gray-300 dark:ring-white/25";
const ALPHA_PILL_CLASSES =
  "inline-flex items-center gap-1 rounded-full bg-v7-white px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-v7-ink shadow-sm ring-1 ring-v7-ink/20";

export const RESTRICTED_PILL_LABEL = "Trading restrictions apply";

/** Popover copy. Deliberately account-agnostic: every country can be restricted. */
export const RESTRICTED_POPOVER_BODY =
  "Some holdings in this product cannot be traded by your account.";
export const RESTRICTED_POPOVER_ACTION = "Click for details";

interface RestrictedPillProps {
  /** Opens the tradability modal. Omitted when `interactive` is false. */
  onClick?: (event: React.MouseEvent) => void;
  /** False when the host element is already a button or an anchor. */
  interactive?: boolean;
  /** Hides the popover when the host row drives its own hover affordance. */
  showPopover?: boolean;
  className?: string;
  appearance?: "default" | "alpha";
}

function PillIcon() {
  return (
    <svg
      className="h-3 w-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 8v5M12 17h.01" />
    </svg>
  );
}

const RestrictedPill: React.FC<RestrictedPillProps> = ({
  onClick,
  interactive = true,
  showPopover = true,
  className = "",
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  const pillClasses = isAlpha ? ALPHA_PILL_CLASSES : PILL_CLASSES;
  const [open, setOpen] = useState(false);
  const [positioned, setPositioned] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>({ top: 0, left: 0 });
  const popoverId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLSpanElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return;
    setPosition(
      clampTooltipPosition(
        trigger.getBoundingClientRect(),
        popover.getBoundingClientRect(),
        "bottom"
      )
    );
  }, []);

  const hide = useCallback(() => {
    setOpen(false);
    setPositioned(false);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    setPositioned(true);
  }, [open, updatePosition]);

  // Reposition rather than dismiss on scroll/resize: several hosts are
  // scrolling containers (the screener table, the analyzer search dropdown),
  // and the pill moves with them.
  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  if (!interactive) {
    return (
      <span className={`inline-block align-middle ${className}`}>
        <span role="img" aria-label={RESTRICTED_PILL_LABEL} className={pillClasses}>
          <PillIcon />
          Restricted
        </span>
      </span>
    );
  }

  return (
    <span
      className={`inline-block align-middle ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={hide}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={RESTRICTED_PILL_LABEL}
        aria-describedby={showPopover && open ? popoverId : undefined}
        onFocus={() => setOpen(true)}
        onBlur={hide}
        onKeyDown={(event) => {
          if (event.key === "Escape" && open) {
            event.stopPropagation();
            hide();
          }
        }}
        onClick={(event) => {
          // The host row navigates or adds on click; the pill must not do either.
          event.stopPropagation();
          event.preventDefault();
          // Close before the modal opens. The modal appears under a stationary
          // cursor, so no mouseleave fires, and Safari does not focus a button
          // on click, so no blur fires either -- the popover would otherwise
          // survive at z-[70], above the modal's z-[60] overlay.
          hide();
          onClick?.(event);
        }}
        className={`${pillClasses} focus:outline-none focus-visible:ring-2 ${isAlpha ? "focus-visible:ring-v7-bronze-dark" : "focus-visible:ring-rp-amber"}`}
      >
        <PillIcon />
        Restricted
      </button>

      {/* Portalled to the body with fixed, viewport-clamped positioning. Hosts
          include overflow-hidden cards and scrolling table wrappers, which
          would clip an absolutely-positioned popover near their edges. */}
      {showPopover && open && typeof document !== "undefined" &&
        createPortal(
          <span
            ref={popoverRef}
            id={popoverId}
            role="tooltip"
            data-appearance={appearance}
            style={{ position: "fixed", top: position.top, left: position.left }}
            className={`pointer-events-none z-[70] block w-64 border p-2.5 text-left shadow-lg transition-opacity duration-150 ${isAlpha ? "rounded-v7-popover border-v7-ink/15 bg-v7-white font-v7-sans" : "rounded-lg border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface-raised"}${
              positioned ? " opacity-100" : " opacity-0"
            }`}
          >
            <span className={`block text-xs font-normal normal-case tracking-normal ${isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300"}`}>
              {RESTRICTED_POPOVER_BODY}
            </span>
            <span className={`mt-1 block text-xs font-semibold normal-case tracking-normal ${isAlpha ? "text-v7-bronze-dark" : "text-rp-amber"}`}>
              {RESTRICTED_POPOVER_ACTION}
            </span>
          </span>,
          document.body
        )}
    </span>
  );
};

export default RestrictedPill;
